/**
 * Builds a GraphQL `imagesAttributes` payload that mirrors the Hike "first index
 * is the cover photo" flow for Hike Lists and Recordings.
 *
 * Kept images are emitted in display order with `isCover: true` on the first
 * entry. Deletions (`_destroy: true`) are appended after the kept entries so
 * the backend processes them but they never participate in the cover decision.
 *
 * Pure module — no Vue / GraphQL / DOM dependencies — so it can be exercised
 * directly from Jest.
 */

export type KeptImage =
  | { kind: 'existing'; id: string; clientId?: string }
  | { kind: 'new-signed'; signedId: string; clientId?: string }
  | { kind: 'new-firebase'; firebaseImageReference: string; clientId?: string };

export type DeletedImage = {
  id: string;
  firebaseImageReference?: string;
  clientId?: string;
};

export interface ImageAttribute {
  id?: string;
  signedId?: string;
  firebaseImageReference?: string;
  parentClientId?: string;
  clientId: string;
  isCover?: boolean;
  _destroy?: boolean;
}

export interface BuildOptions {
  parentClientId?: string;
  generateClientId?: () => string;
}

function defaultClientIdFactory(): () => string {
  return () =>
    typeof window !== 'undefined' && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `img_${Math.random().toString(36).slice(2, 12)}`;
}

export function buildImageAttributesWithCover(
  kept: KeptImage[],
  deletions: DeletedImage[] = [],
  options: BuildOptions = {}
): ImageAttribute[] {
  const generateClientId = options.generateClientId || defaultClientIdFactory();

  const keptAttributes: ImageAttribute[] = kept.map((image, index) => {
    const base: ImageAttribute = {
      clientId: image.clientId || generateClientId(),
      isCover: index === 0,
    };
    if (options.parentClientId) base.parentClientId = options.parentClientId;

    switch (image.kind) {
      case 'existing':
        base.id = image.id;
        break;
      case 'new-signed':
        base.signedId = image.signedId;
        break;
      case 'new-firebase':
        base.firebaseImageReference = image.firebaseImageReference;
        break;
    }
    return base;
  });

  const deletionAttributes: ImageAttribute[] = deletions.map((d) => {
    const attr: ImageAttribute = {
      id: d.id,
      clientId: d.clientId || generateClientId(),
      _destroy: true,
    };
    if (d.firebaseImageReference) attr.firebaseImageReference = d.firebaseImageReference;
    return attr;
  });

  return [...keptAttributes, ...deletionAttributes];
}
